import { Product, ProductVariant } from 'types/Product';
import { FriendlySiteName, SiteName } from 'types/Site';
import BrandTypes from 'constants/brand';
import { GetImageUrl } from './media/media-util';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(global as any).REDIS_INSTANCE_IS_HEALTHY = true;

export const atgOrigin = (window: Window) =>
  window?.location?.origin.indexOf('local') > -1 ? 'http://127.0.0.1:3002' : '';

export const IsObjectEmpty = (empty: object) => Object.keys(empty).length === 0 && empty.constructor === Object;

export function isOnlyDigitInString(string: string) {
  return /(^\d+$)/.test(string);
}

export function stringContainsOnlyAlphanumericCharacters(string: string) {
  return /^[A-Za-z0-9]*$/.test(string);
}

export function getArticle(string: string) {
  if (!/^[a-zA-Z]/.test(string)) {
    throw new Error('First character is not valid');
  }
  if (/^[a,e,u,i,o]/.test(string.toLowerCase())) {
    return `an ${string}`;
  }
  return `a ${string}`;
}

export function transformStockPrimaryForSchema(stockStatus: string | undefined): string | undefined {
  switch (stockStatus) {
    case 'Low stock':
      return 'LimitedAvailability';
    case 'Out of stock':
      return 'OutOfStock';
    case 'Awaiting stock':
      return 'LimitedAvailability';
    case 'In stock':
      return 'InStock';
    case 'Pre-order now':
      return 'PreOrder';
    default:
      return stockStatus;
  }
}

export const getOffersSEOSchema = (skus: ProductVariant[]) =>
  skus.map((sku) => ({
    '@type': 'Offer',
    sku: sku.option,
    price: sku.price?.amount.current && sku.price.amount.current.replace(/^[£€]/, ''),
    priceCurrency: sku.price?.amount.currencyCode && sku.price.amount.currencyCode,
    availability: transformStockPrimaryForSchema(sku.stock?.primary),
    itemCondition: 'http://schema.org/NewCondition',
  }));

export const getSchemaLayerSEO = (product: Product, site: SiteName) => {
  const { skus, description, name, images, reviews } = product;
  const { stars, total } = reviews;
  return `<script type="application/ld+json">
  {
    "@context": "https://schema.org",
    "@type": "Product",
    "offers": ${JSON.stringify(getOffersSEOSchema(skus))},
    "name": "${name}",
    "image": "${GetImageUrl({ identifier: images[0].identifier, template: '$pdp_48x64_x2$', format: 'jpg', site })}",
    "description": "${description}",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "${stars}",
      "reviewCount": "${total}"
    }
  }
  </script>`;
};

export const isLocal =
  process.env.ENVIRONMENT === 'local' || process.env.ENVIRONMENT === 'ci' || process.env.ENVIRONMENT === 'docker';

export const evaluateCachingStatus = () => {
  const cacheEnabled =
    process.env.ENVIRONMENT !== 'ci' &&
    process.env.REDIS_CACHE_ENABLED === 'true' &&
    (global as any).REDIS_INSTANCE_IS_HEALTHY === true;
  return cacheEnabled;
};

export const isNotInStock = (stockCode?: string): boolean => stockCode === 'OUTOFSTOCK' || stockCode === 'UNAVAILABLE';

export const getProductTitle = ({ brand = '', name = '' }): string => `${brand} ${name}`.trim();

export const getTargettedMovsParam = (): boolean => {
  const queryParams = new URLSearchParams(window.location.search);

  const targettedMovsParam = queryParams.get('targettedMovs');
  const isTargettedMovs = targettedMovsParam === 'true';

  return isTargettedMovs;
};

export const getValidSiteValue = (site?: string): SiteName => {
  const lowerCaseSite = site?.toLowerCase();

  if (lowerCaseSite === 'veryireland' || lowerCaseSite === 'veryie') {
    return 'veryie';
  }

  if (lowerCaseSite === 'littlewoods') {
    return 'littlewoods';
  }

  return 'very';
};

export const getFriendlySiteName = (site: SiteName): FriendlySiteName => {
  switch (site) {
    case BrandTypes.VERYIE:
      return 'Very Ireland';
    case BrandTypes.LITTLEWOODS:
      return 'Littlewoods';
    default:
      return 'Very';
  }
};

export const getElasticServiceName = (site: SiteName): string => {
  switch (site) {
    case BrandTypes.VERYIE:
      return 'VeryIE - PDP';
    case BrandTypes.LITTLEWOODS:
      return 'Littlewoods - PDP';
    default:
      return 'Very - PDP';
  }
};

export const getBazaarVoiceUrl = (site: SiteName, environment: string) => {
  switch (site) {
    case BrandTypes.VERYIE:
      return `https://apps.bazaarvoice.com/deployments/littlewoods-ie/main_site/${environment}/en_IE/bv.js`;
    case BrandTypes.LITTLEWOODS:
      return `https://apps.bazaarvoice.com/deployments/littlewoods-gb/main_site/${environment}/en_GB/bv.js`;
    default:
      return `https://apps.bazaarvoice.com/deployments/very-gb/main_site/${environment}/en_GB/bv.js`;
  }
};

export const replaceTitleTagValue = (html: string, newValue = '') =>
  html.replace(/<title>(.*?)<\/title>/, `<title>${newValue}</title>`);

export const getSocialMetaTagsStock = (skus: ProductVariant[]): string => {
  const inStockMessage = 'In stock';
  const awaitingStockMessage = 'Awaiting stock';
  const preOrderMessage = 'Pre-order now';
  const outOfStockMessage = 'Out of stock';
  const inStockStatuses = ['In stock', 'Low stock'];
  const statuses = skus.map((sku) => sku.stock?.primary);
  const validStocks = statuses.filter((stock): stock is string => stock !== undefined);

  if (!validStocks.length) return '';

  const atLeastOneSkuInStock = validStocks.some((el) => inStockStatuses.includes(el));
  const atLeastOneSkuAwaitingStock = validStocks.some((el) => el === awaitingStockMessage);
  const atLeastOneSkuPreOrder = validStocks.some((el) => el === preOrderMessage);

  if (atLeastOneSkuInStock) return inStockMessage;
  if (!atLeastOneSkuInStock && atLeastOneSkuAwaitingStock) return awaitingStockMessage;
  if (!atLeastOneSkuInStock && !atLeastOneSkuAwaitingStock && atLeastOneSkuPreOrder) return preOrderMessage;

  return outOfStockMessage;
};

export const captureClientSideError = (error: Error) => {
  if (error instanceof Error && window?.elasticApm?.captureError) {
    window.elasticApm.captureError(error);
  }
};

export const formatError = (error: unknown, defaultCode = 'UNKNOWN_ERROR'): Error => {
  const errorObj = error instanceof Error ? error : new Error(String(error || 'Unknown error'));

  if (
    !Object.prototype.hasOwnProperty.call(errorObj, 'code') ||
    (errorObj as any).code === undefined ||
    (errorObj as any).code === null
  ) {
    (errorObj as any).code = defaultCode;
  }

  return errorObj;
};

export default {};
